import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Package, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { fetchCart } = useCart();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId && token) {
      pollStatus();
    }
  }, [sessionId, token]);

  const pollStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await fetch(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          fetchCart(); // Refresh cart (should be empty now)
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
      }

      // Still pending, poll again
      setAttempts(prev => prev + 1);
      setTimeout(pollStatus, 2000);
    } catch (e) {
      console.error('Error polling status:', e);
      setAttempts(prev => prev + 1);
      setTimeout(pollStatus, 2000);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <LoadingSpinner size="lg" text="Verifying your payment..." />
        <p className="text-muted-foreground mt-4">Please don't close this page</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-12">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-pastel-stickers/30 flex items-center justify-center animate-scale-in">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-heading font-extrabold mb-4">Payment Successful!</h1>
          <p className="text-muted-foreground mb-8">
            Thank you for your purchase. Your order has been confirmed and the creators have been notified.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/orders">
              <Button className="btn-brutal-primary" data-testid="view-orders-btn">
                <Package className="w-5 h-5 mr-2" />
                View Orders
              </Button>
            </Link>
            <Link to="/browse">
              <Button variant="outline" className="btn-brutal">
                Continue Shopping
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'expired' || status === 'timeout') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-12">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-pastel-crochet/30 flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-500" />
          </div>
          <h1 className="text-3xl font-heading font-extrabold mb-4">
            {status === 'expired' ? 'Session Expired' : 'Verification Timeout'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {status === 'expired' 
              ? 'Your payment session has expired. Please try again.'
              : 'We couldn\'t verify your payment. If you were charged, please contact support.'}
          </p>
          <Link to="/cart">
            <Button className="btn-brutal-primary">
              Return to Cart
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
